// Modal for task acknowledgment with optional notes

import React, { useState } from 'react'
import { SupplierTask } from './TaskCard'
import styles from './TaskAcknowledgmentModal.module.css'

interface TaskAcknowledgmentModalProps {
  task?: SupplierTask
  onAcknowledge: (notes?: string) => Promise<void>
  onCancel: () => void
}

export const TaskAcknowledgmentModal: React.FC<TaskAcknowledgmentModalProps> = ({
  task,
  onAcknowledge,
  onCancel
}) => {
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!task) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      await onAcknowledge(notes.trim() || undefined)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge task')
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (!isSubmitting) {
      onCancel()
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatDate = (date: Date) => {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow'
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      })
    }
  }

  const startTime = new Date(task.startTime)
  const endTime = new Date(task.endTime)
  const now = new Date()
  const isPast = startTime <= now

  return (
    <div className={styles.overlay} onClick={handleCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Acknowledge Task</h2>
          <button 
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className={styles.content}>
          {/* Task summary */}
          <div className={styles.taskSummary}>
            <h3 className={styles.taskTitle}>{task.title}</h3>
            
            <div className={styles.taskDetails}>
              <div className={styles.timeInfo}>
                <span className={styles.date}>{formatDate(startTime)}</span>
                <span className={styles.time}>
                  {formatTime(startTime)} - {formatTime(endTime)}
                </span>
                {task.duration && (
                  <span className={styles.duration}>({task.duration} min)</span>
                )}
              </div>

              {task.location && (
                <div className={styles.location}>
                  <span className={styles.locationIcon}>📍</span>
                  {task.location}
                </div>
              )}
            </div>

            {task.description && (
              <p className={styles.description}>{task.description}</p>
            )}

            {isPast && (
              <div className={styles.overdueWarning}>
                ⚠️ This task has already started. Please acknowledge to confirm your status.
              </div>
            )}
          </div>

          {/* Acknowledgment form */}
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="notes" className={styles.label}>
                Notes (optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={styles.textarea}
                placeholder="Add any notes about this task (optional)..."
                rows={4}
                maxLength={500}
                disabled={isSubmitting}
              />
              <div className={styles.charCount}>
                {notes.length}/500 characters
              </div>
            </div>

            {error && (
              <div className={styles.error}>
                <span className={styles.errorIcon}>⚠️</span>
                {error}
              </div>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.acknowledgeButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className={styles.spinner}></span>
                    Acknowledging...
                  </>
                ) : (
                  <>
                    <span className={styles.checkIcon}>✓</span>
                    Acknowledge Task
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}